import { Icon } from "../../kit";
import { useDomainState, useUi } from "../../../state/editor/useEditor";
import { BASIC_LEFT_RAIL, isBasicLeftPanel, visibleLeftRail, type LeftPanelId } from "../rails/railConfig";
import { LEFT_PANELS } from "./index";
import { BASIC_LEFT_PANELS } from "../basic/rail";
import { InertChip } from "../inspector/rows";
import { RecordingScope } from "../inspector/RecordingScope";
import { LEFT_PANEL_W, RAIL_W } from "../layout";
import { ScrollArea } from "../ScrollArea";

/**
 * The swappable left panel, docked just inside the rail.
 *
 * It opens and closes by SNAPPING, not by animating its width: it floats over
 * the canvas (z-99 chrome), and an animated width would drag a visible seam
 * across the render for no information.
 *
 * Basic mode's panels are the jeweller's presets and carry their own title in
 * the body, the way the approved mockup does; Advanced keeps a header bar with
 * the library view toggle.
 */
export function LeftPanel() {
  const [ui, patchUi] = useUi();
  const selection = useDomainState("selection");
  const basic = ui.inspectorMode === "basic";
  const open = ui.leftPanel;

  if (basic) {
    // An id this rail does not have (an Advanced panel left open across a mode
    // switch, a retired id from an old save) renders nothing rather than guess.
    if (!isBasicLeftPanel(open)) return null;
    const Body = BASIC_LEFT_PANELS[open];
    const title = BASIC_LEFT_RAIL.find((r) => r.id === open)?.label ?? "";
    return (
      <div
        className="pointer-events-auto absolute top-0 bottom-0 z-10 flex flex-col overflow-hidden border-r border-ed-border bg-ed-panel"
        style={{ left: RAIL_W, width: LEFT_PANEL_W }}
        data-basic-left={open}
      >
        {/* pr-3 + the bar's 6px stable gutter = the 18px the left side has. */}
        <ScrollArea className="flex flex-col pb-6 pl-[18px] pr-3 pt-[18px]">
          <div className="mb-3.5 flex min-h-[20px] items-center gap-1.5">
            <h2 className="text-[16px] font-semibold leading-5 text-ed-text">{title}</h2>
          </div>
          <Body />
        </ScrollArea>
      </div>
    );
  }

  // ⚠️ visibleLeftRail, not LEFT_RAIL. `leftPanel` is persisted, so a reload
  // with "metals" saved and nothing selected would otherwise still find the
  // entry and render a material grid with no part to apply it to.
  const meta = visibleLeftRail(!!selection).find((r) => r.id === open);
  const Body = open ? LEFT_PANELS[open as LeftPanelId] : null;
  if (!open || !Body || !meta) return null;

  return (
    <div
      className="pointer-events-auto absolute top-0 bottom-0 z-10 overflow-hidden border-r border-ed-border bg-ed-panel"
      style={{ left: RAIL_W, width: LEFT_PANEL_W }}
      data-advanced-left={open}
    >
      <div className="flex h-full flex-col">
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-ed-border pl-4 pr-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-[15px] font-semibold text-ed-text">{meta.label}</span>
            {meta.inert ? <InertChip tag={meta.inert} /> : null}
          </div>
          <div className="flex items-center gap-0.5">
            <button
              title={ui.libraryView === "card" ? "List view" : "Card view"}
              onClick={() => patchUi({ libraryView: ui.libraryView === "card" ? "list" : "card" })}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-ed-text-3 hover:bg-ed-hover hover:text-ed-text"
            >
              <Icon name={ui.libraryView === "card" ? "view_list" : "view_module"} size={18} />
            </button>
            <button
              title="Close"
              onClick={() => patchUi({ leftPanel: null })}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-ed-text-3 hover:bg-ed-hover hover:text-ed-text"
            >
              <Icon name="close" size={18} />
            </button>
          </div>
        </div>
        <ScrollArea className="flex flex-col">
          <RecordingScope title={meta.label}>
            <Body />
          </RecordingScope>
        </ScrollArea>
      </div>
    </div>
  );
}
